<script>
  import { onMount } from "svelte";
  import {
    AppsRepository,
    UsersRepository,
    GroupsRepository,
  } from "../../repositories/index";
  import { _, callout } from "../../stores/index";
  import { AssignmentGrid } from "../../components/index";
  import { ActionFooter, Loader, Button } from "@gl/design-system";
  import { getContext } from "svelte";
  import { navigate } from "../../utils/index";
  const { isAuthorized } = getContext("isAuthorized");

  export let id;
  export let app;

  let assignmentGrid;

  let appGroups = [];
  let appUsers = [];
  let allUsers = [];
  let allGroups = [];

  let assignmentGridObj = {
    current: [],
    available: [],
  };

  let display = {
    users: true,
    groups: true,
  };

  const loading = {};

  async function fetchAll() {
    try {
      loading.members = true;
      await Promise.all([fetchGroups(), fetchUsers()]).then(() => {
        mapMembers();
      });
    } finally {
      loading.members = false;
    }
  }

  async function fetchUsers() {
    const appUsersList = await AppsRepository.getApplicationUsers(id);
    appUsers = appUsersList.list;

    const usersList = await UsersRepository.getUsers();
    allUsers = usersList.list;
  }

  async function fetchGroups() {
    loading.groups = true;

    const groupsList = await GroupsRepository.getGroups();
    allGroups = groupsList.list;

    const appGroupsList = await AppsRepository.getApplicationGroups(id);
    appGroups = appGroupsList.list;

    appGroups = appGroups.map((g) => ({
      ...g,
      members: allGroups.find((ag) => ag.id === g.id).members,
    }));
  }

  async function mapMembers() {
    assignmentGridObj = {
      available: [],
      current: [],
    };

    if (display.users) {
      const mapUser = (u) => ({
        type: "user",
        id: u.id.toString(),
        label: `${u.firstName} ${u.lastName}`,
        info: u.email,
      });

      const mappedRoleUsers = appUsers.map(mapUser);
      assignmentGridObj.current.push(...mappedRoleUsers);

      const mappedAllUsers = allUsers
        .filter((u) => !appUsers.some((ru) => ru.id === u.id))
        .map(mapUser);
      assignmentGridObj.available.push(...mappedAllUsers);
    }

    if (display.groups) {
      const mapGroup = (g) => ({
        type: "group",
        id: g.id.toString(),
        label: g.name,
        info: `${$_("common.members")} ${g.members}`,
      });

      const mappedRoleGroups = appGroups.map(mapGroup);
      assignmentGridObj.current.push(...mappedRoleGroups);

      const mappedAllGroups = allGroups
        .filter((g) => !appGroups.some((rg) => rg.id === g.id))
        .map(mapGroup);
      assignmentGridObj.available.push(...mappedAllGroups);
    }

    assignmentGridObj.current.sort((a, b) => a.label.localeCompare(b.label));
    assignmentGridObj.available.sort((a, b) => a.label.localeCompare(b.label));
  }

  async function handleSave() {
    loading.save = true;

    const selected = assignmentGrid.getCurrent();

    const usersToAdd = selected.user
      ? selected.user.filter((uID) => !appUsers.some((ru) => ru.id === uID))
      : [];

    const usersToRemove = selected.user
      ? appUsers.filter((ru) => !selected.user.includes(ru.id))
      : appUsers;

    const groupsToAdd = selected.group
      ? selected.group.filter(
          (gID) => !appGroups.some((rg) => rg.id.toString() === gID),
        )
      : [];

    const groupsToRemove = selected.group
      ? appGroups.filter((rg) => !selected.group.includes(rg.id.toString()))
      : appGroups;

    if (
      usersToAdd.length === 0 &&
      usersToRemove.length === 0 &&
      groupsToAdd.length === 0 &&
      groupsToRemove.length === 0
    ) {
      callout.set({
        msg: $_("no.changes.callout"),
        status: "warning",
        timeout: true,
      });
      navigate("/applications");
      return;
    }

    try {
      if (usersToAdd.length)
        await AppsRepository.addApplicationUsers(id, { users: usersToAdd });

      if (usersToRemove.length)
        await AppsRepository.deleteApplicationUsers(id, {
          users: usersToRemove.map((u) => u.id.toString()),
        });

      if (groupsToAdd.length)
        await AppsRepository.addApplicationGroups(id, { groups: groupsToAdd });

      if (groupsToRemove.length)
        await AppsRepository.deleteApplicationGroups(id, {
          groups: groupsToRemove.map((g) => g.id.toString()),
        });

      let calloutMsg = "";
      if (usersToAdd.length > 0)
        calloutMsg += `${usersToAdd.length} ${$_("assignment.callout.users.added")} `;
      if (usersToRemove.length > 0)
        calloutMsg += `${usersToRemove.length} ${$_("assignment.callout.users.removed")} `;
      if (groupsToAdd.length > 0)
        calloutMsg += `${groupsToAdd.length} ${$_("assignment.callout.groups.added")} `;
      if (groupsToRemove.length > 0)
        calloutMsg += `${groupsToRemove.length} ${$_("assignment.callout.groups.removed")} `;

      if (calloutMsg === "") {
        callout.set({
          msg: $_("common.nochanges"),
          status: "success",
          timeout: true,
        });
      } else {
        callout.set({
          msg: calloutMsg,
          status: "success",
          timeout: true,
        });
      }
    } finally {
      fetchAll();
      loading.save = false;
      navigate("/applications");
    }
  }

  onMount(() => {
    fetchAll();
  });
</script>

<svelte:head>
  <title>
    {$_("layout.application.members")}: {app.id} | {$_("common.apps")} | {$_(
      "access-manager",
    )}
  </title>
</svelte:head>

{#if !loading.members}
  <AssignmentGrid
    data={assignmentGridObj}
    bind:this={assignmentGrid}
    disabled={!($isAuthorized("app_write") && $isAuthorized("user_write"))}
  />

  {#if $isAuthorized("app_write") && $isAuthorized("user_write")}
    <ActionFooter>
      <svelte:fragment slot="left">
        <Button on:click={() => assignmentGrid.reset()} outlined
          >{$_("common.reset")}</Button
        >
      </svelte:fragment>
      <Button on:click={handleSave} loading={loading.save}
        >{$_("buttons.save")}</Button
      >
      <Button on:click={() => navigate("/applications")} outlined
        >{$_("buttons.cancel")}</Button
      >
    </ActionFooter>
  {/if}
{:else}
  <Loader status="info" />
{/if}
